import { Box } from '@mui/material';
import { GroupedVirtuoso, type GroupedVirtuosoProps } from 'react-virtuoso';
import { formatFullWeekdayDate } from '../../../../utils/dates';
import type { ResolvedActivityFeedRow } from '../../utils/activityFeedPhrase.types';
import ActivityFeedRow from '../ActivityFeedRow/ActivityFeedRow';
import FeedDateHeader from '../FeedDateHeader/FeedDateHeader';
import type { ActivityFeedDayGroups, ActivityFeedListContext } from './activityFeedList.types';
import { getActivityFeedItemKeys } from './activityFeedList.utils';
import ActivityFeedListFooter from './components/ActivityFeedListFooter';

interface ActivityFeedListProps {
  groups: ActivityFeedDayGroups;
  groupCounts: number[];
  isLoadingMore: boolean;
  scrollParent: HTMLElement | null;
  testId?: string;
  rowTestId?: string;
  rowSnapshotTestId?: string;
  onEndReached: () => void;
}

const FEED_COLUMN_MAX_WIDTH = 640;

const DAY_HEADER_INDENT = 3;

const VIRTUOSO_COMPONENTS: GroupedVirtuosoProps<
  ResolvedActivityFeedRow,
  ActivityFeedListContext
>['components'] = {
  Footer: ActivityFeedListFooter,
};

const ActivityFeedList = ({
  groups,
  groupCounts,
  isLoadingMore,
  scrollParent,
  testId,
  rowTestId,
  rowSnapshotTestId,
  onEndReached,
}: ActivityFeedListProps) => {
  const items = groups.flatMap((group) => group.items);

  const itemKeys = getActivityFeedItemKeys(groups);

  return (
    <Box
      test-id={testId}
      sx={{ width: '100%', maxWidth: FEED_COLUMN_MAX_WIDTH, mx: 'auto', py: 4 }}
    >
      {scrollParent && (
        <GroupedVirtuoso<ResolvedActivityFeedRow, ActivityFeedListContext>
          customScrollParent={scrollParent}
          groupCounts={groupCounts}
          components={VIRTUOSO_COMPONENTS}
          context={{ isLoadingMore }}
          computeItemKey={(index) => itemKeys[index] ?? index}
          endReached={onEndReached}
          groupContent={(groupIndex) => (
            <Box
              sx={{
                bgcolor: 'background.paper',
                pt: groupIndex === 0 ? 0 : 1,
                pb: 1,
                pl: DAY_HEADER_INDENT,
              }}
            >
              <FeedDateHeader label={formatFullWeekdayDate(groups[groupIndex]?.dayKey)} />
            </Box>
          )}
          itemContent={(index) => {
            const item = items[index];

            if (!item) {
              return null;
            }

            return (
              <ActivityFeedRow
                row={item.row}
                phrase={item.phrase}
                testId={rowTestId}
                snapshotTestId={rowSnapshotTestId}
              />
            );
          }}
        />
      )}
    </Box>
  );
};

export default ActivityFeedList;
